import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { styled } from 'styled-components';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { deckFragment } from '@sorare/core/src/hooks/decks/fragments';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';

import { MyBidsBanner } from 'components/auction/MyBidsBanner';

import { CurrentMarketplaceUserProvider as CurrentMarketplaceUserContextProvider } from '.';
import type {
  CurrentMarketplaceUserQuery,
  CurrentMarketplaceUserQueryVariables,
  CurrentMarketplaceUser_currentUser,
} from './__generated__/Provider.graphql';

export const CURRENT_MARKETPLACE_USER_FRAGMENT = gql`
  fragment CurrentMarketplaceUser_currentUser on CurrentUser {
    slug
    wonTokenAuctions(first: 0, sport: [$sport]) {
      hasAny
    }
    boughtSingleSaleTokenOffers(first: 0) {
      hasAny
    }
    cardFilters(sport: $sport)
    userSportProfile: anyUserSportProfile(sport: $sport) {
      id
      decks {
        nodes {
          slug
          ...deckFragments_deck
        }
      }
    }
    myWatchlists(sport: $sport, filter: ALL_WATCHLISTS) {
      slug
      id
      title
    }
    involvedLiveTokenAuctions(after: $after, first: 10, sport: [$sport])
      @include(if: $withMyBidsBanner) {
      ...MyBidsBanner_auctionConnection
    }
  }
  ${MyBidsBanner.fragments.auctions}
  ${deckFragment}
` as TypedDocumentNode<CurrentMarketplaceUser_currentUser>;

const CURRENT_MARKETPLACE_USER_QUERY = gql`
  query CurrentMarketplaceUserQuery(
    $sport: Sport!
    $withMyBidsBanner: Boolean!
    $after: String
  ) {
    currentUser {
      slug
      ...CurrentMarketplaceUser_currentUser
    }
  }
  ${CURRENT_MARKETPLACE_USER_FRAGMENT}
` as TypedDocumentNode<
  CurrentMarketplaceUserQuery,
  CurrentMarketplaceUserQueryVariables
>;

const loggedOutResult = {
  __typename: 'CurrentUser' as const,
  slug: '',
  wonTokenAuctions: {
    __typename: 'TokenAuctionConnection' as const,
    hasAny: false,
  },
  boughtSingleSaleTokenOffers: {
    __typename: 'TokenOfferConnection' as const,
    hasAny: false,
  },
  involvedLiveTokenAuctions: {
    __typename: 'TokenAuctionConnection' as const,
    nodes: [],
    totalCount: 0,
    pageInfo: {
      __typename: 'PageInfo' as const,
      hasNextPage: false,
      endCursor: null,
    },
  },
  cardFilters: [],
  userSportProfile: null,
  myWatchlists: [],
};

const Root = styled(Vertical)`
  min-height: calc(100vh - var(--current-stack-height));
`;

const ContentWrapper = styled.div`
  margin-bottom: auto;
`;

type BidsBannerWrapperProps = {
  children?: ReactNode;
  auctions: CurrentMarketplaceUser_currentUser['involvedLiveTokenAuctions'];
  loading: boolean;
  loadMore: (
    loadMore: boolean,
    options: { after: string | undefined | null }
  ) => void;
};

const BidsBannerWrapper = ({
  children,
  auctions,
  loading,
  loadMore,
}: BidsBannerWrapperProps) => (
  <Root>
    <ContentWrapper>{children}</ContentWrapper>
    <MyBidsBanner auctions={auctions} loading={loading} loadMore={loadMore} />
  </Root>
);

export const CurrentMarketplaceUserProvider = ({
  children,
  sport,
  withMyBidsBanner = true,
}: {
  children?: ReactNode;
  sport: Sport;
  withMyBidsBanner?: boolean;
}) => {
  const { data, loading, loadMore } = usePaginatedQuery(
    CURRENT_MARKETPLACE_USER_QUERY,
    {
      variables: { sport, withMyBidsBanner },
      connections: ['TokenAuctionConnection'],
    }
  );

  if (!data) return null;

  return (
    <CurrentMarketplaceUserContextProvider
      value={data.currentUser || loggedOutResult}
    >
      <ConditionalWrapper
        wrap={withMyBidsBanner}
        Wrapper={BidsBannerWrapper}
        props={{
          auctions: data.currentUser?.involvedLiveTokenAuctions,
          loading,
          loadMore,
        }}
      >
        {children}
      </ConditionalWrapper>
    </CurrentMarketplaceUserContextProvider>
  );
};
